/*
  Wheel smoothing, measured against the reference rather than guessed at.

  What the reference actually does: its <html> carries `lenis lenis-enabled`, a
  synthetic (untrusted) wheel event moves the page 1200px - which no native
  scroll can do - and even window.scrollTo({behavior:'auto'}) is animated across
  ~90 frames. So the reference runs Lenis and patches programmatic scrolling.
  Fitting the decay of its own trace gives a per-frame lerp of ~0.11, i.e. the
  Lenis default of 0.1, settling in roughly 700-800ms. On a touch viewport the
  class is absent entirely: the reference leaves touch scrolling native.

  This reproduces the feel without the dependency, and deliberately stops short
  of the parts of Lenis that cause collateral damage:

    * only `wheel` is intercepted. window.scrollTo, scrollIntoView, anchor
      navigation, history and scroll restoration are never patched, so they stay
      exactly as native - the reference's own scrollTo('auto') being animated is
      a bug we are not copying.
    * no keydown handling, so Page Up/Down, Home/End, space and arrows keep
      their native behaviour.
    * touch and coarse pointers are left alone, which preserves iOS momentum.
    * reduced motion turns it off completely.
    * a wheel over anything that can scroll itself - a dialog, a drawer, a rail,
      a <select> - is handed straight back to the browser.
    * `position: sticky` keeps working because real scrollTop is what moves.
*/
(() => {
  const root = document.documentElement;
  if (!('requestAnimationFrame' in window)) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = window.matchMedia('(pointer: coarse)');

  const LERP = 0.1;          // measured on the reference
  const EPSILON = 0.4;       // px; below this we land and stop

  let target = null;         // null => we are not animating
  let frame = 0;
  let ours = false;          // the scroll event we are about to cause is ours
  let wrote = null;          // the exact scrollY we last set ourselves
  let rafAlive = null;       // null unknown, true proven, false give up
  let probing = false;
  let watchdog = 0;

  /*
    We only ever call preventDefault() once we have watched a real animation
    frame run, because a frame callback is not guaranteed to fire: a background
    or occluded tab parks rAF indefinitely. Claiming the wheel and then never
    getting a frame would leave the page unscrollable, so until rAF has proved
    itself the gesture is handed back to the browser and we just start a probe
    for next time. Going hidden invalidates the proof.
  */
  const probeRaf = () => {
    if (probing || rafAlive === false) return;
    probing = true;
      requestAnimationFrame(() => { probing = false; rafAlive = true; });
  };

  const maxScroll = () =>
    Math.max(0, (document.scrollingElement || root).scrollHeight - window.innerHeight);

  const stop = () => {
    if (frame) cancelAnimationFrame(frame);
    if (watchdog) clearTimeout(watchdog);
    frame = 0;
    watchdog = 0;
    target = null;
    wrote = null;
  };

  const step = () => {
    frame = 0;
    if (watchdog) { clearTimeout(watchdog); watchdog = 0; }
    rafAlive = true;
    if (target === null) return;
    const current = window.scrollY;
    const delta = target - current;
    if (Math.abs(delta) < EPSILON) {
      ours = true;
      wrote = target;
      window.scrollTo(0, target);
      target = null;
      return;
    }
    ours = true;
    wrote = current + delta * LERP;
    window.scrollTo(0, wrote);
    frame = requestAnimationFrame(step);
  };

  /*
    Does something under the pointer own this VERTICAL gesture? Only a vertical
    scroller can, and only while it still has room to move the way the wheel is
    pointing - at its end the gesture chains back to the page.

    A horizontal rail is explicitly NOT a vertical scroll consumer. Treating one
    as a reason to decline is what broke the page: over "Hot This Week",
    "Targeted formulas" and "You May Also Like" this returned true for every
    wheel, so the engine handed the whole event to the browser, which passed it
    to the rail's `scroll-snap-type: x mandatory` scroller. A pure vertical wheel
    survived on native chaining, but a trackpad gesture with any sideways
    component - measured at deltaX 40 / deltaY 300 - was swallowed whole and the
    page moved 0px. Horizontal-dominant gestures never reach here: onWheel hands
    those to the browser before this runs, which is what keeps the rails usable.
  */
  const verticalOwner = (node, deltaY) => {
    for (let el = node; el && el !== document.body && el !== root; el = el.parentElement || el.parentNode?.host) {
      if (el.nodeType !== 1) continue;
      if (el.closest && el.closest('dialog')) return true;
      if (el.hasAttribute('data-no-smooth-scroll')) return true;
      const tag = el.tagName;
      if (tag === 'SELECT' || tag === 'TEXTAREA') return true;
      const style = getComputedStyle(el);
      const oy = style.overflowY;
      if (oy === 'auto' || oy === 'scroll') {
        const room = el.scrollHeight - el.clientHeight;
        if (room > 1) {
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop >= room - 1;
          if (!(deltaY < 0 && atTop) && !(deltaY > 0 && atBottom)) return true;
        }
      }
    }
    return false;
  };

  const onWheel = (event) => {
    if (reduce.matches || coarse.matches) return;
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey) return;                 // pinch zoom
    if (event.deltaMode !== 0) return;                          // page/line wheels: native
    if (!event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (document.querySelector('dialog[open]')) return;         // drawers, cart, quick view
    // The theme locks scrolling by putting `scroll-locked` on <html>, not <body>.
    if (root.hasAttribute('data-scroll-locked')
      || root.classList.contains('scroll-locked')
      || document.body.classList.contains('scroll-locked')) return;
    if (verticalOwner(event.target, event.deltaY)) return;

    const limit = maxScroll();
    if (limit <= 0) return;
    const from = target === null ? window.scrollY : target;
    const next = Math.min(limit, Math.max(0, from + event.deltaY));
    // Already pinned against the end of the document: let the browser do its
    // own overscroll/chaining rather than swallowing the gesture.
    if (next === from && (from <= 0 || from >= limit)) return;

    if (rafAlive !== true) { probeRaf(); return; }   // native this time

    event.preventDefault();
    target = next;
    if (!frame) frame = requestAnimationFrame(step);
    // If that frame never arrives, land on the target and hand scrolling back
    // rather than swallowing the gesture.
    if (!watchdog) {
      watchdog = setTimeout(() => {
        watchdog = 0;
        if (target !== null) { ours = true; wrote = target; window.scrollTo(0, target); target = null; }
        rafAlive = false;
      }, 250);
    }
  };

  /*
    Anything that scrolls the page without going through us - a scrollbar drag,
    the keyboard, an anchor jump, scroll restoration, a script calling
    scrollIntoView - cancels the animation so the two never fight.

    Attribution is by POSITION, not by a boolean. The browser coalesces scroll
    events within a frame, so a programmatic scroll landing in the same frame
    as one of our own writes arrives as a SINGLE scroll event. A flag then
    credits that one event to us, the external scroll goes unnoticed, and the
    next frame drags the page back to our stale target - the anchor jump, or
    the editor's scroll-to-section, silently undone.

    Measured: with the flag alone, a scrollIntoView issued while a wheel
    animation was still settling was reverted 2 times out of 2, the page
    snapping back 296px. Comparing where the page actually is against the last
    value we wrote cannot be fooled that way, because an external scroll by
    definition does not land where we put it.
  */
  const onScroll = () => {
    if (target === null) { ours = false; wrote = null; return; }
    if (ours && wrote !== null && Math.abs(window.scrollY - wrote) <= 1.5) { ours = false; return; }
    stop();
  };

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('pointerdown', stop, { passive: true });
  window.addEventListener('keydown', stop, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') { stop(); rafAlive = null; }
  });
  const off = () => { if (reduce.matches || coarse.matches) stop(); };
  reduce.addEventListener?.('change', off);
  coarse.addEventListener?.('change', off);
})();
